import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { useAuth } from "../lib/auth";
import { PATHS, fmtDay, useLang, useT } from "../lib/i18n";
import { Seo } from "../components/site/Seo";
import { useAlts } from "../components/site/Layout";
import { TipCard, TipGrid } from "../components/site/TipCard";
import { Crumbs, Faq, MonthTable, RecordChart, RecordStats, Section, VipCta } from "../components/site/Blocks";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import TournamentPage from "./TournamentPage";
import VipPage from "./VipPage";
import HomePage from "../components/site/Home";

export const Wrap = ({ children, className = "" }) => <div className={`mx-auto max-w-7xl px-4 py-10 sm:px-6 ${className}`}>{children}</div>;
const Html = ({ html, testid }) => (html ? <div className="prose-tt" data-testid={testid} dangerouslySetInnerHTML={{ __html: html }} /> : null);

function MoneyPage({ p }) {
  const lang = useLang();
  const t = useT();
  const d = p.data;
  const P = PATHS[lang];
  if (d.home) return <HomePage p={p} />;
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="court-lines absolute inset-0" />
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[var(--ball)]/10 blur-3xl" />
        <Wrap className="relative grid gap-10 pb-16 pt-14 lg:grid-cols-[1.3fr_1fr] lg:items-center">
          <div>
            {p.path !== `/${lang}/` && <Crumbs items={p.crumbs} />}
            <p className="rise text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ball)]">{t("Geprüft · Begründet · Täglich", "Reviewed · Reasoned · Daily")}</p>
            <h1 className="rise rise-2 mt-3 font-display text-5xl font-extrabold leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="page-h1">{d.page.h1}</h1>
            <p className="rise rise-3 mt-5 max-w-xl text-base text-muted-foreground md:text-lg" data-testid="page-intro">{d.page.lead}</p>
            <div className="rise rise-4 mt-7 flex flex-wrap gap-3">
              <Link to={P.register} data-testid="hero-register-btn"><Button size="lg" className="rounded-full px-7 font-semibold">{t("Kostenlos registrieren & weitere Tipps sehen", "Join free & unlock more tips")} <ArrowRight size={16} className="ml-1" /></Button></Link>
              {d.record && <Link to={P.record}><Button size="lg" variant="secondary" className="rounded-full px-7" data-testid="hero-record-btn">{t("Bilanz ansehen", "View record")}</Button></Link>}
            </div>
          </div>
          <div className="rise rise-3">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><span className="h-2 w-2 rounded-full bg-[var(--ball)] live-dot" />{t("Gratis-Tipp heute", "Free tip today")}</p>
            {d.free_tips[0] ? <TipCard tip={d.free_tips[0]} /> : <div className="surface p-8 text-center text-muted-foreground" data-testid="free-tip-empty">{t("Der heutige Gratis-Tipp folgt in Kürze.", "Today's free tip is coming soon.")}</div>}
          </div>
        </Wrap>
      </section>
      <Wrap>
        <RecordStats total={d.record?.total} gate={d.stats_gate} />
        <Section title={t("Neueste Tipps", "Latest tips")} sub={t("VIP-Tipps sind gesperrt – Ergebnisse sind öffentlich.", "VIP tips are locked – results are public.")} testid="latest-tips">
          <TipGrid tips={d.tips} />
        </Section>
        {d.record && (
          <Section title={t("Öffentliche Bilanz", "Public track record")} testid="record-section" action={<Link to={P.record} className="text-sm text-[var(--ball)]">{t("Alle Details", "Full details")} →</Link>}>
            <div className="grid gap-4 lg:grid-cols-2"><RecordChart series={d.record.series} /><MonthTable rows={d.record.by_month} label={t("Monat", "Month")} /></div>
          </Section>
        )}
        {d.page.body_html && <section className="mt-16 max-w-3xl"><Html html={d.page.body_html} testid="page-body" /></section>}
        <VipCta products={d.products} />
        <Faq items={p.faq} />
      </Wrap>
    </>
  );
}

function RecordPage({ p }) {
  const t = useT();
  const r = p.data.record;
  if (!r) return (
    <Wrap className="max-w-3xl">
      <Crumbs items={p.crumbs} />
      <h1 className="font-display text-5xl font-extrabold sm:text-6xl" data-testid="page-h1">{t("Bilanz", "Record")}</h1>
      <div className="surface mt-8 p-5 text-sm text-muted-foreground" data-testid="record-pending">{t("Die Bilanz wird hier veröffentlicht, sobald genügend Tipps ausgewertet sind.", "The record will be published here once enough tips have been settled.")}</div>
      <VipCta />
    </Wrap>
  );
  return (
    <Wrap>
      <Crumbs items={p.crumbs} />
      <h1 className="font-display text-5xl font-extrabold sm:text-6xl" data-testid="page-h1">{p.data.page.h1}</h1>
      <div className="mt-3 max-w-2xl"><Html html={p.data.page.body_html} /></div>
      <div className="mt-8"><RecordStats total={r.total} /></div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 font-num text-sm">
        {[["Units +", r.total.units_won], ["Units −", r.total.units_lost], ["ROI (100u)", `${r.total.roi}%`], [t("Ø Quote", "Avg odds"), r.total.avg_odds]].map(([l, v]) => (
          <div key={l} className="surface p-4"><p className="text-xs text-muted-foreground">{l}</p><p className="mt-1 text-xl font-bold">{v}</p></div>
        ))}
      </div>
      <Section title={t("Verlauf", "Profit curve")}><RecordChart series={r.series} /></Section>
      <Section title={t("Pro Monat", "Per month")}><MonthTable rows={r.by_month} label={t("Monat", "Month")} /></Section>
      <Section title={t("Pro Tour", "Per tour")}><MonthTable rows={r.by_tour} keyName="tour" label="Tour" /></Section>
      <Section title={t("Letzte Ergebnisse", "Recent results")}><TipGrid tips={p.data.recent} /></Section>
      <VipCta />
    </Wrap>
  );
}

function TourPage({ p }) {
  const lang = useLang();
  const t = useT();
  const d = p.data;
  return (
    <Wrap>
      <Crumbs items={p.crumbs} />
      <h1 className="font-display text-5xl font-extrabold" data-testid="page-h1">{p.meta.title}</h1>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tournament-list">
        {d.tournaments.map((x) => (
          <Link key={x.id} to={x.url} className="surface surface-hover block p-5" data-testid={`tournament-link-${x.slug}`}>
            <p className="text-xs uppercase tracking-wider text-[var(--ball)]">{x.category}</p>
            <p className="mt-1 font-display text-2xl font-bold">{x.name}</p>
            <p className="mt-2 text-sm text-muted-foreground">{x.city} · {x.surface} · {fmtDay(x.start_date, lang)}</p>
          </Link>
        ))}
        {!d.tournaments.length && <p className="text-muted-foreground">{t("Turniere folgen.", "Tournaments coming soon.")}</p>}
      </div>
      <Section title={t("Aktuelle Tipps", "Current tips")}><TipGrid tips={d.tips} /></Section>
    </Wrap>
  );
}

function BlogPage({ p }) {
  const lang = useLang();
  return (
    <Wrap>
      <Crumbs items={p.crumbs} />
      <h1 className="font-display text-5xl font-extrabold" data-testid="page-h1">{p.data.page.h1}</h1>
      <div className="mt-3 max-w-2xl"><Html html={p.data.page.body_html} /></div>
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {!p.data.posts.length && <p className="text-muted-foreground" data-testid="posts-empty">{lang === "de" ? "Noch keine Beiträge." : "No posts yet."}</p>}
        {p.data.posts.map((x) => (
          <Link key={x.id} to={`${p.data.base || `/${lang}/news/`}${x.slug}/`} className="surface surface-hover overflow-hidden" data-testid={`post-link-${x.slug}`}>
            {x.cover_url && <img src={x.cover_url} alt={x.title} className="aspect-video w-full object-cover" loading="lazy" />}
            <div className="p-5">{!p.data.base && <p className="text-xs text-muted-foreground">{fmtDay(x.published_at, lang)}</p>}<h2 className="mt-1 font-display text-2xl font-bold">{x.title}</h2><p className="mt-2 text-sm text-muted-foreground">{x.excerpt}</p></div>
          </Link>
        ))}
      </div>
    </Wrap>
  );
}

function PostPage({ p }) {
  const lang = useLang();
  const x = p.data.post;
  return (
    <Wrap className="max-w-3xl">
      <Crumbs items={p.crumbs} />
      {!p.data.guide && <p className="text-xs text-muted-foreground">{fmtDay(x.published_at, lang)}</p>}
      <h1 className="mt-2 font-display text-5xl font-extrabold" data-testid="page-h1">{x.title}</h1>
      {x.cover_url && <img src={x.cover_url} alt={x.title} className="mt-6 aspect-video w-full rounded-2xl object-cover" />}
      <div className="mt-8"><Html html={x.body_html} testid="post-body" /></div>
      <Faq items={p.faq} />
      <VipCta />
    </Wrap>
  );
}

const LegalPage = ({ p }) => (
  <Wrap className="max-w-3xl">
    <Crumbs items={p.crumbs} />
    <h1 className="font-display text-5xl font-extrabold" data-testid="page-h1">{p.data.page.h1}</h1>
    <div className="mt-8"><Html html={p.data.page.body_html} testid="legal-body" /></div>
  </Wrap>
);

function NotFound() {
  const t = useT();
  const lang = useLang();
  return (
    <Wrap className="py-32 text-center">
      <Trophy className="mx-auto text-[var(--ball)]" size={48} />
      <h1 className="mt-4 font-display text-6xl font-extrabold">404</h1>
      <p className="mt-2 text-muted-foreground">{t("Seite nicht gefunden.", "Page not found.")}</p>
      <Link to={PATHS[lang].home}><Button className="mt-6 rounded-full">{t("Zur Startseite", "Home")}</Button></Link>
    </Wrap>
  );
}

function LoadError({ retry }) {
  const t = useT();
  return (
    <Wrap className="py-32 text-center" data-testid="page-load-error">
      <h1 className="font-display text-4xl font-extrabold">{t("Verbindungsproblem", "Connection problem")}</h1>
      <p className="mt-2 text-muted-foreground">{t("Die Seite konnte nicht geladen werden.", "The page could not be loaded.")}</p>
      <Button className="mt-6 rounded-full" onClick={retry} data-testid="page-retry-btn">{t("Erneut versuchen", "Try again")}</Button>
    </Wrap>
  );
}

const VIEWS = { money: MoneyPage, record: RecordPage, tour: TourPage, blog: BlogPage, post: PostPage, legal: LegalPage, tournament: TournamentPage, vip: VipPage };

export default function PublicPage() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { setAlts } = useAlts();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(0);
  const authReady = user !== null;
  useEffect(() => {
    if (!authReady) return;
    setErr(0);
    api.get("/page", { params: { path: pathname } }).then((r) => { setP(r.data); setAlts(r.data.meta.alternates); window.scrollTo(0, 0); })
      .catch((e) => (e?.response?.status === 404 ? setP({ type: "notfound", meta: { title: "404", robots: "noindex" } }) : setErr(1)));
  }, [pathname, authReady, setAlts, err === 2]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(() => api.get("/page", { params: { path: pathname } }).then((r) => { if (r.data.type !== "redirect") setP(r.data); }).catch(() => {}), 60000, authReady && !!p && p.type !== "notfound");
  if (err === 1) return <LoadError retry={() => setErr(2)} />;
  if (!p) return <Wrap><Skeleton className="h-16 w-2/3" /><Skeleton className="mt-6 h-64 w-full" /></Wrap>;
    if (p.type === "redirect") return <Navigate to={p.to} replace />;
  const View = VIEWS[p.type] || NotFound;
  return <><Seo page={p} /><View p={p} /></>;
}
